import React, { useMemo } from 'react';
import {
  useGetCalendarMeetingsQuery,
  useGetTasksListQuery,
} from '@/redux/internal-services/workspaceApiExtensions';
import { Spinner } from '@/components/UI/Spinner';
import MonthView from '@/components/Calendar/MonthView';
import TimeGrid from '@/components/Calendar/TimeGrid';
import {
  weekRange, startOfDay, endOfDay, toISO, dateKey,
} from '@/components/Calendar/calendarUtils';
import type { CalView } from '@/components/Calendar/calendarUtils';
import WeatherAlertBanner from '@/components/Calendar/WeatherAlertBanner';
import { useAppDispatch, useAppSelector } from '@/hooks';
import {
  navigateCalendarAction,
  setCalendarAnchor,
  setCalendarView,
} from '@/redux/slices/calendarNavSlice';

// ── Range helpers ────────────────────────────────────────────────────────────

function rangeFor(anchor: Date, view: CalView) {
  if (view === 'month') {
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const last  = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { from: toISO(startOfDay(first)), to: toISO(endOfDay(last)) };
  }
  if (view === 'week') {
    const { from, to } = weekRange(anchor);
    return { from: toISO(from), to: toISO(to) };
  }
  return { from: toISO(startOfDay(anchor)), to: toISO(endOfDay(anchor)) };
}

function headerLabel(anchor: Date, view: CalView) {
  if (view === 'month') return anchor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  if (view === 'week') {
    const { days } = weekRange(anchor);
    const first = days[0]; const last = days[6];
    if (first.getMonth() === last.getMonth())
      return `${first.toLocaleDateString(undefined, { month: 'long' })} ${first.getDate()}–${last.getDate()}, ${first.getFullYear()}`;
    return `${first.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${last.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`;
  }
  return anchor.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
}

// ── Page ─────────────────────────────────────────────────────────────────────

interface CalendarPageProps {
  onTaskClick?: (taskId: string) => void;
  onMeetingClick?: (meetingId: string) => void;
}

const CalendarPage: React.FC<CalendarPageProps> = ({ onTaskClick, onMeetingClick }) => {
  const dispatch = useAppDispatch();
  const { anchorISO, view } = useAppSelector((s) => s.calendarNav);

  // Parse anchor from ISO string — noon to avoid DST boundary issues.
  const anchor = useMemo(() => new Date(anchorISO + 'T12:00:00'), [anchorISO]);

  const { from, to } = rangeFor(anchor, view);

  const { data: meetings = [], isLoading: ml } = useGetCalendarMeetingsQuery({ start_from: from, start_to: to });
  const { data: taskData, isLoading: tl } = useGetTasksListQuery({
    due_date_from: from, due_date_to: to, limit: 200,
  });

  const tasks = taskData?.data ?? [];
  const isLoading = ml || tl;

  const days = view === 'week'
    ? weekRange(anchor).days
    : [anchor];

  const ViewBtn: React.FC<{ v: CalView; label: string }> = ({ v, label }) => (
    <button
      type="button"
      onClick={() => dispatch(setCalendarView(v))}
      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
        view === v
          ? 'bg-brand-dark text-white'
          : 'text-ui-text2 hover:bg-ui-hover'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#EDEDED] flex-shrink-0">
        {/* Today */}
        <button
          type="button"
          onClick={() => dispatch(navigateCalendarAction({ direction: 'today' }))}
          className="px-3 py-1.5 text-xs font-medium rounded-lg border border-[#EDEDED] text-ui-text hover:bg-ui-hover transition-colors"
        >
          Today
        </button>

        {/* Prev / Next */}
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => dispatch(navigateCalendarAction({ direction: 'prev', unit: view }))}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-ui-hover transition-colors text-ui-text2"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <button
            type="button"
            onClick={() => dispatch(navigateCalendarAction({ direction: 'next', unit: view }))}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-ui-hover transition-colors text-ui-text2"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        {/* Period label */}
        <h2 className="text-sm font-semibold text-ui-text flex-1">{headerLabel(anchor, view)}</h2>

        {/* Loading indicator */}
        {isLoading && <Spinner size="sm" />}

        {/* View switcher */}
        <div className="flex items-center bg-ui-subtle rounded-lg p-0.5 border border-[#EDEDED]">
          <ViewBtn v="day"   label="Day" />
          <ViewBtn v="week"  label="Week" />
          <ViewBtn v="month" label="Month" />
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-3 px-4 py-1.5 border-b border-[#EDEDED] flex-shrink-0 flex-wrap">
        <span className="flex items-center gap-1 text-[10px] text-ui-text3">
          <span className="w-3 h-3 rounded bg-brand-dark inline-block" /> Meeting
        </span>
        <span className="flex items-center gap-1 text-[10px] text-ui-text3">
          <span className="w-3 h-3 rounded bg-teal-500 inline-block" /> Site Visit
        </span>
        <span className="flex items-center gap-1 text-[10px] text-ui-text3">
          <span className="w-3 h-3 rounded bg-amber-500 inline-block" /> Inspection
        </span>
        <span className="flex items-center gap-1 text-[10px] text-ui-text3">
          <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> Task
        </span>
        <span className="flex items-center gap-1 text-[10px] text-ui-text3">
          <span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Overdue
        </span>
      </div>

      {/* Weather alerts */}
      <WeatherAlertBanner />

      {/* Calendar body — flex-1 fills remaining height; min-h-0 ensures the flex child can
          shrink below its content height so TimeGrid's inner overflow-y-auto can scroll. */}
      <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
        {view === 'month' ? (
          <MonthView
            anchor={anchor}
            tasks={tasks}
            meetings={meetings}
            onDayClick={(d) => {
              dispatch(setCalendarAnchor(dateKey(d)));
              dispatch(setCalendarView('day'));
            }}
            onTaskClick={onTaskClick}
            onMeetingClick={onMeetingClick}
          />
        ) : (
          <TimeGrid days={days} tasks={tasks} meetings={meetings} onTaskClick={onTaskClick} onMeetingClick={onMeetingClick} />
        )}
      </div>
    </div>
  );
};

export default CalendarPage;
